import type { Product, ProductFeed } from '../types/product';

// Simple formula evaluator for basic expressions
function evaluateFormula(formula, formData, form) {
  if (!formula || typeof formula !== 'string') {
    return 0;
  }

  // For now, assume the formula is a field reference (like 'energy_score')
  // This can be extended later to support complex expressions
  const fieldValue = formData[formula];

  if (fieldValue === undefined || fieldValue === null) {
    return 0;
  }
  if (typeof fieldValue === 'number') {
    return fieldValue;
  }
  if (typeof fieldValue === 'string') {
    const numValue = parseFloat(fieldValue);
    const result = isNaN(numValue) ? 0 : numValue;
    return result;
  }

  return 0;
}

function evalOperator(operator, a, b) {
  let result;
  if (operator === 'equals') {
    result = a == b;
  } else if (operator === 'contains') {
    result = typeof a === 'string' && a.includes(b);
  } else if (operator === '>') {
    result = Number(a) > Number(b);
  } else if (operator === '>=') {
    result = Number(a) >= Number(b);
  } else if (operator === '<') {
    result = Number(a) < Number(b);
  } else if (operator === '<=') {
    result = Number(a) <= Number(b);
  } else if (operator === 'closest_to') {
    // For closest_to, we need special handling
    // This will be used to find the closest available value
    const targetValue = Number(b);
    const actualValue = Number(a);

    // For now, we'll allow any value and handle scoring later
    // The distance will be calculated: Math.abs(actualValue - targetValue)
    result = !isNaN(actualValue) && !isNaN(targetValue);
  } else {
    result = false;
  }

  return result;
}

function evalGroup(group, product, formData, form) {
  if (!group.rules.length) {
    return true;
  }

  const results = group.rules.map((rule, index) => {
    let result;
    // Get the field/attribute name - support both field and attribute properties
    const fieldName = rule.field || rule.attribute;

    // Check if this is a formula field (by checking if field exists in formData)
    if (fieldName && formData && formData[fieldName] !== undefined) {
      const formulaValue = evaluateFormula(fieldName, formData, form);
      result = evalOperator(rule.operator, formulaValue, rule.value);
    } else {
      // Original attribute-based logic for product attributes
      const attr = product.attributeValues?.[fieldName];
      result = evalOperator(rule.operator, attr, rule.value);
    }

    return result;
  });

  const finalResult = group.rules[group.logic === 'AND' ? 'every' : 'some'](
    (rule, index) => results[index]
  );

  return finalResult;
}

function evalGroups(groups, betweenGroupLogic, product, formData, form) {
  if (!groups.length) {
    return true;
  }

  let result = evalGroup(groups[0], product, formData, form);

  for (let i = 1; i < groups.length; i++) {
    const logic = betweenGroupLogic[i - 1] || 'OR';
    const groupResult = evalGroup(groups[i], product, formData, form);

    if (logic === 'AND') {
      result = result && groupResult;
    } else {
      result = result || groupResult;
    }
  }

  return result;
}

export function useProductAdviseMatching(productFeed, matchingRules, formData, form) {
  // Voor elk product: check of hij matcht met de regels voor de gegeven antwoorden
  if (!productFeed || !matchingRules || !formData) {
    return [];
  }

  // Convert formData array to object if needed
  let formDataObject = formData;
  if (Array.isArray(formData)) {
    formDataObject = {};
    formData.forEach((item) => {
      if (item.fieldId && item.value !== undefined) {
        formDataObject[item.fieldId] = item.value;
      }
    });
  }

  // Group rules by questionId to handle multiple answers for same question with OR logic
  const groupedRules = matchingRules.reduce(
    (groups, rule) => {
      if (!groups[rule.questionId]) {
        groups[rule.questionId] = [];
      }
      groups[rule.questionId].push(rule);
      return groups;
    },
    {} as Record<string, typeof matchingRules>
  );

  const matchedProducts = productFeed.filter((product, productIndex) => {
    // Voor elke vraag (groep van regels): check of minstens één regel matcht (OR binnen vraag)
    const questionResults = Object.entries(groupedRules).map(([questionId, rules]) => {
      const answer = formDataObject[questionId];

      // Check if any rule for this question matches (OR logic within same question)
      const questionMatches = rules.some((rule, ruleIndex) => {
        let ruleApplies = false;

        if (rule.answerValue !== undefined) {
          // Voor choice: alleen matchen als het antwoord overeenkomt met de optie
          if (Array.isArray(answer)) {
            ruleApplies = answer.includes(rule.answerValue);
          } else {
            ruleApplies = answer === rule.answerValue;
          }

          if (!ruleApplies) {
            return false; // Try next rule for this question
          }
        } else if (rule.answerCondition) {
          // Voor number/slider: check operator
          if (rule.answerCondition.operator === 'between') {
            ruleApplies =
              answer >= rule.answerCondition.value[0] && answer <= rule.answerCondition.value[1];
          } else {
            ruleApplies = evalOperator(
              rule.answerCondition.operator,
              answer,
              rule.answerCondition.value
            );
          }

          if (!ruleApplies) {
            return false; // Try next rule for this question
          }
        } else {
          ruleApplies = true;
        }

        // Nu: check of het product voldoet aan de groepen
        const groupsResult = evalGroups(
          rule.groups,
          rule.betweenGroupLogic,
          product,
          formDataObject,
          form
        );

        return groupsResult;
      });

      return questionMatches;
    });

    // Product matches if ALL questions match (AND between different questions)
    const productMatches = questionResults.every((result) => result);

    return productMatches;
  });

  return matchedProducts;
}

// New scoring-based product matching system
export function useProductAdviseMatchingScoring(
  productFeed,
  matchingRules,
  formData,
  form,
  settings = {}
) {
  // Default settings
  const { scoreThreshold = 0, maxProducts = 10, masterRules = [] } = settings;

  // Adjust overlapping between ranges before processing
  const adjustedRules = adjustOverlappingRanges([...matchingRules]);

  if (!productFeed || !matchingRules || !formData) {
    return [];
  }

  // Check if any rules reference non-existent form data keys and migrate old format
  const migratedRules = matchingRules.map((rule) => {
    // Migrate old format to new format if needed
    if (!rule.weight) rule.weight = 1;
    if (rule.exactMatch === undefined) rule.exactMatch = false;
    return rule;
  });

  // Group rules by questionId
  const groupedRules = adjustedRules.reduce((groups, rule) => {
    if (!groups[rule.questionId]) {
      groups[rule.questionId] = {
        rules: [],
        weight: rule.weight || 1,
        exactMatch: rule.exactMatch || false,
        questionLabel: rule.questionLabel || rule.questionId,
      };
    }
    groups[rule.questionId].rules.push(rule);
    // Use the highest weight if multiple rules for same question
    if (rule.weight > groups[rule.questionId].weight) {
      groups[rule.questionId].weight = rule.weight;
    }
    // If any rule requires exact match, the whole question requires exact match
    if (rule.exactMatch) {
      groups[rule.questionId].exactMatch = true;
    }
    return groups;
  }, {});

  // Calculate scores for all products
  const scoredProducts = productFeed.map((product, productIndex) => {
    const shouldDebug = product.title.includes('Base');

    // First check master rules - if any fail, exclude product completely
    let passedMasterRules = true;
    if (masterRules.length > 0) {
      passedMasterRules = masterRules.every((masterRule) => {
        const result = evalGroups([masterRule], [], product, formData, form);
        return result;
      });

      if (!passedMasterRules) {
        return {
          product,
          score: 0,
          maxPossibleScore: 0,
          excluded: true,
          reason: 'Failed master rules',
        };
      }
    }

    let totalScore = 0;
    let maxPossibleScore = 0;
    const questionScores = {};

    // Evaluate each question group
    for (const [questionId, questionGroup] of Object.entries(groupedRules)) {
      const { rules, weight, exactMatch, questionLabel } = questionGroup;
      const answer = formData[questionId];

      maxPossibleScore += weight;

      // Check if any rule for this question matches (OR logic within same question)
      const questionMatches = rules.some((rule, ruleIndex) => {
        let ruleApplies = false;

        // Check if answer matches rule conditions
        if (rule.answerValue !== undefined) {
          if (Array.isArray(answer)) {
            ruleApplies = answer.includes(rule.answerValue);
          } else {
            ruleApplies = answer === rule.answerValue;
          }
        } else if (rule.answerCondition) {
          if (rule.answerCondition.operator === 'between') {
            ruleApplies =
              answer >= rule.answerCondition.value[0] && answer <= rule.answerCondition.value[1];
          } else {
            ruleApplies = evalOperator(
              rule.answerCondition.operator,
              answer,
              rule.answerCondition.value
            );
          }
        } else {
          ruleApplies = true;
        }

        if (!ruleApplies) {
          return false;
        }

        const groupsResult = evalGroups(
          rule.groups,
          rule.betweenGroupLogic,
          product,
          formData,
          form
        );

        // Special handling for closest_to rules
        let finalResult = groupsResult;
        if (groupsResult && rule.groups) {
          const hasClosestToRule = rule.groups.some(
            (group) => group.rules && group.rules.some((r) => r.operator === 'closest_to')
          );

          if (hasClosestToRule) {
            // For closest_to rules, we'll calculate a proximity score
            // This will be used later in the scoring phase
            finalResult = true; // Allow the rule to pass, scoring will handle the rest
          }
        }

        return finalResult;
      });

      questionScores[questionId] = {
        matched: questionMatches,
        weight,
        exactMatch,
        questionLabel,
        score: questionMatches ? weight : 0,
      };

      if (questionMatches) {
        totalScore += weight;
      } else {
        // If exact match required and no match, exclude product
        if (exactMatch) {
          return {
            product,
            score: 0,
            maxPossibleScore,
            excluded: true,
            reason: `Failed exact match for: ${questionLabel}`,
            questionScores,
          };
        }
      }
    }

    const scorePercentage = maxPossibleScore > 0 ? (totalScore / maxPossibleScore) * 100 : 0;

    return {
      product,
      score: totalScore,
      maxPossibleScore,
      scorePercentage,
      excluded: false,
      questionScores,
    };
  });

  // Filter out excluded products and apply threshold
  const validProducts = scoredProducts.filter(
    (item) => !item.excluded && item.score >= scoreThreshold
  );

  // Apply closest_to post-processing
  const processedProducts = applyClosestToLogic(validProducts, adjustedRules, formData);

  // Sort by score (highest first) and limit results
  const sortedProducts = processedProducts.sort((a, b) => b.score - a.score).slice(0, maxProducts);

  return sortedProducts;
}

// Helper function to adjust overlapping between ranges
function adjustOverlappingRanges(rules) {
  const betweenRules = rules.filter(
    (rule) => rule.answerCondition && rule.answerCondition.operator === 'between'
  );

  if (betweenRules.length < 2) return rules; // No overlaps possible

  // Sort by start value
  betweenRules.sort((a, b) => a.answerCondition.value[0] - b.answerCondition.value[0]);

  // Adjust overlapping ranges
  for (let i = 0; i < betweenRules.length - 1; i++) {
    const current = betweenRules[i];
    const next = betweenRules[i + 1];

    const currentEnd = current.answerCondition.value[1];
    const nextStart = next.answerCondition.value[0];

    // If ranges overlap (current end >= next start)
    if (currentEnd >= nextStart) {
      // Adjust current range to end at nextStart - 0.01
      current.answerCondition.value[1] = nextStart - 0.01;
    }
  }

  return rules;
}

// Helper function to apply closest_to logic
function applyClosestToLogic(products, matchRules, formData = {}) {
  // Find all closest_to rules that actually apply based on form answers
  const closestToRules = [];
  matchRules.forEach((rule) => {
    // Check if this rule applies based on the user's answer
    let ruleApplies = false;

    if (rule.answerValue !== undefined) {
      const answer = formData[rule.questionId];
      if (Array.isArray(answer)) {
        ruleApplies = answer.includes(rule.answerValue);
      } else {
        ruleApplies = answer === rule.answerValue;
      }
    } else if (rule.answerCondition) {
      const answer = formData[rule.questionId];
      if (rule.answerCondition.operator === 'between') {
        ruleApplies =
          answer >= rule.answerCondition.value[0] && answer <= rule.answerCondition.value[1];
      } else {
        // For other operators, check if the condition matches
        ruleApplies = true; // We'll assume it applies for now
      }
    } else {
      ruleApplies = true; // No answer condition, always applies
    }

    // Only process closest_to rules that actually apply
    if (ruleApplies && rule.groups) {
      rule.groups.forEach((group) => {
        if (group.rules) {
          group.rules.forEach((condition) => {
            if (condition.operator === 'closest_to') {
              closestToRules.push({
                questionId: rule.questionId,
                attribute: condition.attribute || condition.field,
                targetValue: Number(condition.value),
                weight: rule.weight || 1,
                ruleDescription: `${rule.questionId}: ${rule.answerValue || JSON.stringify(rule.answerCondition)}`,
              });
            }
          });
        }
      });
    }
  });

  if (closestToRules.length === 0) {
    return products; // No closest_to rules, return as-is
  }

  // Group products by the closest_to attributes to find the best match for each attribute
  const processedProducts = products.map((productItem) => {
    let adjustedScore = productItem.score;

    closestToRules.forEach((rule) => {
      const productValue = Number(productItem.product.attributeValues?.[rule.attribute]);

      if (!isNaN(productValue) && !isNaN(rule.targetValue)) {
        const distance = Math.abs(productValue - rule.targetValue);

        // Find the minimum distance among all products for this rule
        const allDistances = products
          .map((p) => {
            const val = Number(p.product.attributeValues?.[rule.attribute]);
            return isNaN(val) ? Infinity : Math.abs(val - rule.targetValue);
          })
          .filter((d) => d !== Infinity);

        const minDistance = Math.min(...allDistances);

        // If this product has the minimum distance, give it a bonus
        // If not, reduce its score based on how far it is from the target
        if (distance === minDistance) {
          // Perfect or best match - give full points
        } else {
          // Not the best match - reduce score proportionally
          const maxDistance = Math.max(...allDistances);
          const scorePenalty = rule.weight * (distance / maxDistance);
          adjustedScore -= scorePenalty;
        }
      }
    });

    const finalScore = Math.max(0, adjustedScore);

    return {
      ...productItem,
      score: finalScore, // Don't go below 0
      originalScore: productItem.score,
    };
  });

  return processedProducts;
}

// Helper function to calculate match percentages per question for UI charts
export function calculateQuestionMatchPercentages(
  productFeed,
  matchingRules,
  formData,
  form,
  questionId
) {
  if (!productFeed || !matchingRules || !formData) {
    return { totalProducts: 0, matchedProducts: 0, percentage: 0 };
  }

  // Filter rules for this specific question
  const questionRules = matchingRules.filter((rule) => rule.questionId === questionId);

  if (questionRules.length === 0) {
    return { totalProducts: productFeed.length, matchedProducts: 0, percentage: 0 };
  }

  let matchedCount = 0;

  productFeed.forEach((product) => {
    const answer = formData[questionId];

    // Check if any rule for this question matches this product
    const questionMatches = questionRules.some((rule) => {
      let ruleApplies = false;

      // Check if answer matches rule conditions
      if (rule.answerValue !== undefined) {
        if (Array.isArray(answer)) {
          ruleApplies = answer.includes(rule.answerValue);
        } else {
          ruleApplies = answer === rule.answerValue;
        }
      } else if (rule.answerCondition) {
        if (rule.answerCondition.operator === 'between') {
          ruleApplies =
            answer >= rule.answerCondition.value[0] && answer <= rule.answerCondition.value[1];
        } else {
          ruleApplies = evalOperator(
            rule.answerCondition.operator,
            answer,
            rule.answerCondition.value
          );
        }
      } else {
        ruleApplies = true;
      }

      if (!ruleApplies) {
        return false;
      }

      // Check if product matches the rule's groups
      return evalGroups(rule.groups, rule.betweenGroupLogic, product, formData, form);
    });

    if (questionMatches) {
      matchedCount++;
    }
  });

  const percentage = productFeed.length > 0 ? (matchedCount / productFeed.length) * 100 : 0;

  return {
    totalProducts: productFeed.length,
    matchedProducts: matchedCount,
    percentage: Math.round(percentage * 10) / 10, // Round to 1 decimal
  };
}
